<template>
    <div ref="chart" class="distribution-chart"></div>
</template>

<script>
import * as echarts from "echarts";

const C_SCOPE1 = "#b88330";
const C_SCOPE2 = "#1e7a63";
const C_SCOPE3 = "#3a7fc1";
const C_HAIRLINE = "#ddd6c7";
const C_INK_MUTE = "#8b8f8a";
const C_INK_FAINT = "#b6b6ad";

export default {
    name: "FacilityDistributionTab",
    props: {
        enriched: { type: Array, default: () => [] },
        apiUnit: { type: String, default: "kgCO2e" },
        displayUnit: { type: String, default: "kgCO2e" },
    },
    data() {
        return { chart: null };
    },
    computed: {
        maxTotal() {
            return Math.max(...this.enriched.map((f) => f.total ?? 0), 1);
        },
        yoyValues() {
            return this.enriched.filter((f) => !f.isNew && f.yoy != null).map((f) => f.yoy);
        },
        yMin() {
            const v = this.yoyValues.length ? Math.min(...this.yoyValues) : 0;
            return Math.min(v - 8, -5);
        },
        yMax() {
            const v = this.yoyValues.length ? Math.max(...this.yoyValues) : 0;
            const bump = this.enriched.some((f) => f.isNew) ? 14 : 8;
            return Math.max(v + bump, 5);
        },
    },
    mounted() {
        this.initChart();
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.handleResize);
        this.chart?.dispose();
    },
    watch: {
        enriched() {
            this.$nextTick(this.refreshChart);
        },
    },
    methods: {
        initChart() {
            if (!this.$refs.chart) return;
            this.chart?.dispose();
            this.chart = echarts.init(this.$refs.chart);
            window.addEventListener("resize", this.handleResize);
            this.refreshChart();
        },
        handleResize() {
            this.chart?.resize();
        },
        refreshChart() {
            if (!this.chart) return;
            this.chart.setOption(this.buildOption(), { notMerge: true });
        },
        buildOption() {
            const { enriched, yMin, yMax, maxTotal } = this;
            let newSlot = 0;

            const data = enriched.map((f) => {
                const ratio = Math.max(f.ratio ?? 0.001, 0.001);
                const chartY = f.isNew ? yMax - 4 - newSlot++ * 6 : f.yoy;
                const rNorm = Math.sqrt((f.total ?? 0) / maxTotal);
                const dotSize = Math.max(8, 8 + rNorm * 32);
                const color = f.scope === 2 ? C_SCOPE2 : f.scope === 3 ? C_SCOPE3 : C_SCOPE1;
                const name = f.facilityName ?? f.name ?? "";
                const short = name.length > 7 ? name.slice(0, 7) + "…" : name;
                const isLarge = ratio >= 1.0;
                const flipLabel = ratio > 50;
                const yoyTag = f.isNew
                    ? "{new|신규}"
                    : f.yoy >= 0
                      ? `{yUp|+${f.yoy.toFixed(1)}%}`
                      : `{yDn|${f.yoy.toFixed(1)}%}`;

                return {
                    value: [ratio, chartY, f.total ?? 0, f.scope, name, f.isNew, f.yoy],
                    symbolSize: dotSize,
                    symbol: f.isNew ? "emptyCircle" : "circle",
                    itemStyle: { color, opacity: f.isNew ? 0.7 : 0.86 },
                    label: {
                        show: isLarge,
                        position: flipLabel ? "left" : "right",
                        distance: 6,
                        formatter: `{nm|${short}}\n${yoyTag}`,
                    },
                };
            });

            return {
                backgroundColor: "transparent",
                tooltip: {
                    trigger: "item",
                    backgroundColor: "rgba(11, 17, 25, 0.88)",
                    borderColor: "transparent",
                    borderRadius: 12,
                    padding: [10, 16],
                    textStyle: { color: "#fff" },
                    formatter: (params) => {
                        if (params.componentType !== "series") return null;
                        const [ratio, , total, scope, name, isNew, yoy] = params.data.value;
                        let val = Number(total);
                        if (this.apiUnit === "kgCO2e" && this.displayUnit === "tCO2e") val = val / 1000;
                        else if (this.apiUnit === "tCO2e" && this.displayUnit === "kgCO2e") val = val * 1000;
                        const unit = this.displayUnit === "tCO2e" ? "t" : "kg";
                        const tStr = `${Math.round(val).toLocaleString("ko-KR")} ${unit}`;
                        const yoyHtml = isNew
                            ? `<span style="background:rgba(251,191,36,.15);color:#fbbf24;padding:1px 7px;border-radius:999px;font-size:10px">신규 집계</span>`
                            : `<span style="color:${yoy >= 0 ? "#fbbf24" : "#34d399"}">${yoy >= 0 ? "▲" : "▼"}${Math.abs(yoy).toFixed(1)}% YoY</span>`;
                        return (
                            `<div style="font-size:10px;color:#94a3b8;letter-spacing:.08em;margin-bottom:5px">${name}</div>` +
                            `<div style="font-size:22px;font-weight:700;letter-spacing:-.02em;color:#fff">` +
                            `${ratio.toFixed(1)}<span style="font-size:14px;opacity:.65">%</span></div>` +
                            `<div style="margin-top:7px;display:flex;align-items:center;gap:8px;font-size:11px;font-weight:600">` +
                            `${yoyHtml}<span style="color:#64748b;margin-left:auto">S${scope} · ${tStr}</span></div>`
                        );
                    },
                },
                grid: { left: 44, right: 40, top: 15, bottom: 30 },
                xAxis: {
                    type: "log",
                    logBase: 10,
                    name: "배출 비중 →",
                    nameLocation: "end",
                    nameGap: 4,
                    nameTextStyle: { color: C_INK_MUTE, fontSize: 8.5, fontWeight: 500 },
                    axisLine: { show: false },
                    axisTick: { show: false },
                    axisLabel: { color: C_INK_MUTE, fontSize: 8, formatter: (v) => `${v}%` },
                    splitLine: { show: false },
                },
                yAxis: {
                    type: "value",
                    min: yMin,
                    max: yMax,
                    axisLine: { show: false },
                    axisTick: { show: false },
                    axisLabel: { color: C_INK_MUTE, fontSize: 8, formatter: (v) => `${v}%` },
                    splitLine: { lineStyle: { color: "#f0ece5", type: "dashed" } },
                },
                series: [
                    {
                        type: "scatter",
                        data,
                        emphasis: { scale: 1.2, focus: "self" },
                        blur: { itemStyle: { opacity: 0.15 } },
                        label: {
                            rich: {
                                nm: { color: "#11161a", fontSize: 10, fontWeight: 700 },
                                yUp: { color: "#c07820", fontSize: 9, fontWeight: 700 },
                                yDn: { color: "#1e7a63", fontSize: 9, fontWeight: 700 },
                                new: { color: "#c07820", fontSize: 9, fontWeight: 700 },
                            },
                        },
                        markArea: {
                            silent: true,
                            emphasis: { disabled: true },
                            data: [
                                [
                                    {
                                        name: "주요 · 증가 ↗",
                                        coord: [10, 0],
                                        itemStyle: { color: "rgba(192,120,32,0.10)", borderWidth: 0 },
                                        label: {
                                            position: "insideTopLeft",
                                            offset: [6, 6],
                                            color: "#c07820",
                                            fontSize: 9,
                                            fontWeight: 700,
                                            opacity: 0.8,
                                        },
                                    },
                                    { coord: [200, yMax + 10] },
                                ],
                                [
                                    {
                                        name: "소규모 · 증가",
                                        coord: [0.005, 0],
                                        itemStyle: { color: "rgba(192,120,32,0.04)", borderWidth: 0 },
                                        label: {
                                            position: "insideTopLeft",
                                            offset: [6, 6],
                                            color: C_INK_FAINT,
                                            fontSize: 9,
                                            fontWeight: 700,
                                        },
                                    },
                                    { coord: [10, yMax + 10] },
                                ],
                                [
                                    {
                                        name: "주요 · 감소 ↘",
                                        coord: [10, yMin - 10],
                                        itemStyle: { color: "rgba(30,122,99,0.07)", borderWidth: 0 },
                                        label: {
                                            position: "insideBottomLeft",
                                            offset: [6, -6],
                                            color: "#1e7a63",
                                            fontSize: 9,
                                            fontWeight: 700,
                                            opacity: 0.8,
                                        },
                                    },
                                    { coord: [200, 0] },
                                ],
                                [
                                    {
                                        name: "소규모 · 양호",
                                        coord: [0.005, yMin - 10],
                                        itemStyle: { color: "rgba(215,208,196,0.20)", borderWidth: 0 },
                                        label: {
                                            position: "insideBottomLeft",
                                            offset: [6, -6],
                                            color: C_INK_FAINT,
                                            fontSize: 9,
                                            fontWeight: 700,
                                        },
                                    },
                                    { coord: [10, 0] },
                                ],
                            ],
                        },
                        markLine: {
                            silent: true,
                            symbol: ["none", "none"],
                            label: { show: false },
                            data: [
                                { yAxis: 0, lineStyle: { color: C_HAIRLINE, width: 1, type: "solid" } },
                                { xAxis: 10, lineStyle: { color: C_HAIRLINE, width: 1, type: "dashed" } },
                            ],
                        },
                    },
                ],
            };
        },
    },
};
</script>

<style scoped>
.distribution-chart {
    flex: 1;
    min-height: 0;
    min-width: 0;
}
</style>
